import { create } from "zustand";
import { ServiceContainer } from "@/core/di";
import type { KybModulePolicy } from "@/core/administration/entities/KybModulePolicy";

const sc = ServiceContainer.getInstance();
const repo = sc.getRepository("kybModulePolicy");

export interface KybModuleState {
  /**
   * Whether the KYB module is active. Defaults to `true` until the first
   * status fetch resolves. A failed/slow status check must never block
   * onboarding for everyone by defaulting closed.
   */
  enabled: boolean;
  statusLoaded: boolean;
  policy: KybModulePolicy | null;
  policyLoading: boolean;
  fetchStatus: () => Promise<void>;
  fetchPolicy: () => Promise<void>;
  updatePolicy: (enabled: boolean) => Promise<boolean>;
}

export const useKybModuleStore = create<KybModuleState>()((set) => ({
  enabled: true,
  statusLoaded: false,
  policy: null,
  policyLoading: false,

  fetchStatus: async () => {
    const result = await repo.getStatus();
    if (result.success && result.data) {
      set({ enabled: result.data.enabled, statusLoaded: true });
    } else {
      // Endpoint down/not-yet-shipped: fail open, not closed.
      set({ statusLoaded: true });
    }
  },

  fetchPolicy: async () => {
    set({ policyLoading: true });
    const result = await repo.getPolicy();
    if (result.success && result.data) {
      set({ policy: result.data, enabled: result.data.enabled });
    }
    set({ policyLoading: false });
  },

  updatePolicy: async (enabled: boolean) => {
    const result = await repo.updatePolicy(enabled);
    if (result.success && result.data) {
      set({ policy: result.data, enabled: result.data.enabled });
      return true;
    }
    return false;
  },
}));
